import { HouseComposerView } from "../viewer/HouseComposerView.ts";
import type { HouseComposition } from "../types.ts";

export class ClientApp {
  private view: HouseComposerView;

  /** Called with 0..1 as rooms load; call with 1 to signal completion. */
  onProgress: ((fraction: number, label: string) => void) | null = null;

  constructor(
    container: HTMLElement,
    private token: string,
    composition: HouseComposition,
  ) {
    this.view = new HouseComposerView(container, {
      visibleModes: ["dollhouse", "plan", "panorama"],
      showPanoramaMarkers: false,
    });

    this.view.getPanoramaBlobUrl = async (captureId, _filename) => {
      const url = `/api/client/${encodeURIComponent(token)}/captures/${encodeURIComponent(captureId)}/media`;
      try {
        const res = await fetch(url);
        if (!res.ok) return null;
        const blob = await res.blob();
        return URL.createObjectURL(blob);
      } catch {
        return null;
      }
    };

    for (const pano of composition.panoramas ?? []) this.view.addPanorama(pano);
    for (const m of composition.measurements ?? []) this.view.addMeasurementEntry(m);
    for (const n of composition.notes ?? []) this.view.addNoteEntry(n);

    void this._loadContent(composition);
  }

  private async _loadContent(composition: HouseComposition): Promise<void> {
    const rooms = composition.rooms ?? [];
    const floorplans = composition.floorplans ?? [];

    const indices = rooms.map(entry => this.view.registerRoom(entry));

    const visible = rooms.filter(r => r.visible);
    const total = visible.length;

    if (total > 0) this.onProgress?.(0, `Loading model (0/${total})`);

    let done = 0;
    for (let i = 0; i < rooms.length; i++) {
      if (!rooms[i].visible) continue;
      const url = `/api/client/${encodeURIComponent(this.token)}/captures/${encodeURIComponent(rooms[i].capture_id)}/media`;
      await this.view.loadRoomModel(indices[i], url);
      done++;
      this.onProgress?.(
        done / total,
        done < total ? `Loading model (${done}/${total})` : "",
      );
    }

    for (const fp of floorplans) {
      if (!fp.visible) continue;
      const url = `/api/client/${encodeURIComponent(this.token)}/captures/${encodeURIComponent(fp.capture_id)}/media`;
      this.view.addFloorplan(fp, url);
    }
  }

  dispose(): void {
    this.view.dispose();
  }
}
